import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { withCeoPassword } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Search, Save, FileCheck, Edit, Trash2, Printer, Send, Inbox } from "lucide-react";
import SearchableSelect from "@/components/SearchableSelect";
import TableExportButton from "@/components/TableExportButton";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

export default function Store() {
  const { user } = useAuth();
  const [poNumber, setPoNumber] = useState("");
  const [po, setPo] = useState(null);
  const [priorReceipts, setPriorReceipts] = useState([]); // prior store entries on the same PO (continuation)
  const [poFullyReceived, setPoFullyReceived] = useState(null); // { po, grns:[...] } — shown when nothing left to inward
  const [entries, setEntries] = useState([]);
  const [edit, setEdit] = useState(null); // store entry being edited (or new)
  const [departments, setDepartments] = useState([]);
  const [intenders, setIntenders] = useState([]);
  const [issueDialog, setIssueDialog] = useState(null); // { entry, lines: [{item_index, quantity}], department, step:1|2 }
  const [logQuery, setLogQuery] = useState("");        // search across the Logs/Receipts table (GRN / PO / Supplier / Item)
  const [logFromDate, setLogFromDate] = useState(""); // "" = no lower bound
  const [logToDate, setLogToDate] = useState("");     // "" = no upper bound
  // Extra filters (Feb-2026) — Company / Category / Cost Center / Supplier.
  // Each works independently and combines with the search + date range.
  const [logCompany, setLogCompany] = useState("");
  const [logCategory, setLogCategory] = useState("");
  const [logCostCenter, setLogCostCenter] = useState("");
  const [logSupplier, setLogSupplier] = useState("");
  const [companyOptions, setCompanyOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [costCenterOptions, setCostCenterOptions] = useState([]);
  const [supplierOptions, setSupplierOptions] = useState([]);
  const navigate = useNavigate();

  const load = async () => {
    const [e, d, ins, coR, catR, ccR, supR] = await Promise.all([
      api.get("/store-entries"),
      api.get("/issue-departments"),
      api.get("/intenders").catch(() => ({ data: [] })),
      api.get("/companies-book").catch(() => ({ data: [] })),
      api.get("/categories").catch(() => ({ data: [] })),
      api.get("/cost-centers").catch(() => ({ data: [] })),
      api.get("/suppliers").catch(() => ({ data: [] })),
    ]);
    setEntries(e.data);
    setDepartments(d.data || []);
    setIntenders(ins.data || []);
    setCompanyOptions((coR.data || []).map((c) => c.name).filter(Boolean));
    setCategoryOptions((catR.data || []).map((c) => c.name).filter(Boolean));
    setCostCenterOptions((ccR.data || []).map((c) => c.name).filter(Boolean));
    setSupplierOptions((supR.data || []).map((s) => s.name).filter(Boolean));
  };
  useEffect(() => { load(); }, []);

  // Filtered receipts — combines search query + date range + 4 dropdown filters.
  // Reused by both the table and the "Total Value" summary + export.
  const filteredLogEntries = React.useMemo(() => {
    const q = logQuery.trim().toLowerCase();
    return (entries || []).filter((e) => {
      // Date range filter — uses grn_date (falls back to created_at date part)
      if (logFromDate || logToDate) {
        const rowDate = (e.grn_date || (e.created_at || "").slice(0, 10)) || "";
        if (logFromDate && rowDate < logFromDate) return false;
        if (logToDate && rowDate > logToDate) return false;
      }
      // Dropdown filters
      if (logCompany && (e.company_name || "") !== logCompany) return false;
      if (logCostCenter && (e.cost_center || "") !== logCostCenter) return false;
      if (logSupplier && (e.supplier_name || "") !== logSupplier) return false;
      if (logCategory) {
        const hasCat = (e.items || []).some((it) => (it.category || "") === logCategory);
        if (!hasCat) return false;
      }
      // Search filter
      if (q) {
        const hay = [
          e.grn_number, e.po_number, e.supplier_name, e.supplier_gst, e.company_name, e.cost_center,
          ...((e.items || []).flatMap((it) => [it.description, it.item_code, it.category])),
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [entries, logQuery, logFromDate, logToDate, logCompany, logCategory, logCostCenter, logSupplier]);

  const logTotalGrossValue = React.useMemo(() => {
    return filteredLogEntries.reduce((sum, e) => sum + Number(e.gross_total || 0), 0);
  }, [filteredLogEntries]);

  const fetchPo = async () => {
    if (!poNumber.trim()) return;
    try {
      const { data } = await api.get(`/po-by-number/${poNumber.trim()}`);
      setPo(data);
      // Find prior store entries for this same PO (continuation)
      const prior = (entries || []).filter((e) => e.po_number === data.po_number);
      setPriorReceipts(prior);
      // Only show items that still have pending quantity left to inward
      const pendingRes = await api.get(`/purchase-orders/${data.id}/pending-items`).catch(() => null);
      const pending = pendingRes?.data || [];
      if (pending.length === 0) {
        // Nothing left to inward — but the user may still need to book
        // multiple supplier bills against the SAME PO (e.g. GRN #1 covers
        // items A+B, and each item arrived with its own supplier invoice).
        // Show a helpful panel listing every existing GRN on this PO with
        // a shortcut to book/view a bill for each one — instead of the
        // previous dead-end "already inwarded" toast.
        const grnRows = prior
          .filter((r) => r.grn_number)
          .map((r) => ({
            id: r.id,
            grn_number: r.grn_number,
            grn_date: r.grn_date,
            supplier_name: r.supplier_name,
            supplier_invoice_number: r.supplier_invoice_number,
            actual_total: r.actual_total || 0,
            gross_total: r.gross_total || 0,
            items_summary: (r.items || []).map((it) => `${it.description || it.item_code || "—"} × ${it.actual_quantity}`).join(", "),
          }));
        setPoFullyReceived({ po: data, grns: grnRows });
        setEdit(null);
        return;
      }
      setPoFullyReceived(null);
      setEdit({
        po_number: data.po_number,
        notes: "",
        supplier_name: data.supplier?.name || "",
        supplier_gst: data.supplier?.gst || "",
        invoice_number: "",
        invoice_date: "",
        vehicle_number: "",
        challan_dc: "",
        items: pending.map((it) => ({
          item_id: "",
          item_code: it.item_code || "",
          description: it.description,
          unit: it.unit,
          hsn_code: it.hsn_sac || "",
          po_quantity: it.pending_quantity, // pending qty becomes the "PO Qty" reference for this GRN run
          po_price: it.price,
          actual_quantity: "", // BLANK by default — user must enter manually
          actual_price: it.price,
          actual_value: 0,
          taxation: 0,
          gross_value: 0,
          include: true, // default checked; user can uncheck to leave the item in balance
        })),
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "PO not found");
      setPo(null);
      setPriorReceipts([]);
      setPoFullyReceived(null);
    }
  };

  const updateItem = (idx, patch) => {
    setEdit((p) => {
      const items = [...p.items];
      const merged = { ...items[idx], ...patch };
      // Auto-recompute Actual Value & Gross Value whenever inputs change
      const aq = Number(merged.actual_quantity || 0);
      const ap = Number(merged.actual_price || 0);
      const taxPct = Number(merged.taxation || 0); // taxation stored as PERCENT
      const av = Number((aq * ap).toFixed(2));
      const taxAmt = Number((av * taxPct / 100).toFixed(2));
      merged.actual_value = av;
      merged.tax_amount = taxAmt;
      merged.gross_value = Number((av + taxAmt).toFixed(2));
      items[idx] = merged;
      return { ...p, items };
    });
  };

  const totalActual = (edit?.items || []).filter((it) => it.include !== false).reduce((s, it) => s + Number(it.actual_value || 0), 0);
  const totalGross = (edit?.items || []).filter((it) => it.include !== false).reduce((s, it) => s + Number(it.gross_value || 0), 0);
  const poTotal = po?.grand_total || 0;
  const exceeds = po && totalActual > poTotal + 0.01;

  const save = async ({ generateGrn = false } = {}) => {
    if (exceeds) return toast.error("Actual value exceeds PO total");
    if (!edit.invoice_number?.trim()) return toast.error("Invoice Number is required");
    // Only items marked "include" are received in this entry. Unchecked items
    // stay in the pending list (PO remains in BALANCE) for a future receipt.
    const includedItems = (edit.items || []).filter((it) => it.include !== false);
    if (includedItems.length === 0) {
      return toast.error("Select at least one item to receive (check the checkbox)");
    }
    // Actual Quantity must be manually entered for every checked line item
    const missingAq = includedItems.findIndex(
      (it) => it.actual_quantity === "" || it.actual_quantity === null || it.actual_quantity === undefined || Number(it.actual_quantity) <= 0
    );
    if (missingAq !== -1) {
      return toast.error(`Enter Actual Quantity for "${includedItems[missingAq].description}" — it cannot be blank or zero`);
    }
    try {
      const payload = {
        po_number: edit.po_number,
        // Strip the `include` flag before sending to the backend
        items: includedItems.map(({ include, ...rest }) => rest),
        notes: edit.notes || "",
        supplier_name: edit.supplier_name || "",
        supplier_gst: edit.supplier_gst || "",
        invoice_number: edit.invoice_number || "",
        invoice_date: edit.invoice_date || "",
        vehicle_number: edit.vehicle_number || "",
        challan_dc: edit.challan_dc || "",
        grn_date: edit.grn_date || "",
      };
      let saved;
      if (edit.id) {
        saved = (await api.put(`/store-entries/${edit.id}`, payload)).data;
        toast.success("Store entry updated");
      } else {
        saved = (await api.post("/store-entries", payload)).data;
        toast.success("Store entry saved");
      }
      if (generateGrn) {
        if (!saved.grn_generated) {
          await api.post(`/store-entries/${saved.id}/generate-grn`);
        }
        toast.success("GRN generated · Stock updated");
        setEdit(null);
        setPo(null);
        setPoNumber("");
        load();
        navigate(`/grn/print/${saved.id}`);
        return;
      }
      setEdit(null);
      setPo(null);
      setPoNumber("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const generateGrnFor = async (entry) => {
    try {
      await api.post(`/store-entries/${entry.id}/generate-grn`);
      toast.success("GRN generated · Stock updated");
      load();
      navigate(`/grn/print/${entry.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to generate GRN");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this store entry? If issue slips exist, you'll be prompted for the CEO password.")) return;
    try {
      await withCeoPassword((cfg) => api.delete(`/store-entries/${id}`, cfg));
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const editExisting = (entry) => {
    // Default the editable date to TODAY when the GRN doesn't have one yet
    // (e.g. a freshly-fetched PO entry being booked). When the entry already
    // has a grn_date / created_at, keep that — the user can still change it.
    const todayISO = new Date().toISOString().slice(0, 10);
    const existingDate = entry.grn_date || (entry.created_at ? entry.created_at.slice(0, 10) : "");
    setEdit({
      ...entry,
      supplier_name: entry.supplier_name || "",
      supplier_gst: entry.supplier_gst || "",
      invoice_number: entry.invoice_number || "",
      invoice_date: entry.invoice_date || "",
      vehicle_number: entry.vehicle_number || "",
      challan_dc: entry.challan_dc || "",
      // Editable GRN/Inward date — defaults to today when missing.
      grn_date: existingDate || todayISO,
      items: (entry.items || []).map((it) => {
        const aq = Number(it.actual_quantity || 0);
        const ap = it.actual_price != null ? Number(it.actual_price) : (aq ? Number((it.actual_value / aq).toFixed(2)) : 0);
        const taxPct = Number(it.taxation || 0);
        const av = Number((aq * ap).toFixed(2));
        const taxAmt = Number((av * taxPct / 100).toFixed(2));
        return {
          ...it,
          hsn_code: it.hsn_code || "",
          actual_price: ap,
          actual_value: av,
          taxation: taxPct,
          tax_amount: taxAmt,
          gross_value: Number((av + taxAmt).toFixed(2)),
        };
      }),
    });
    setPo({ grand_total: entry.po_total, po_number: entry.po_number });
    setPoNumber(entry.po_number);
  };

  const startIssue = (entry) => {
    if (!entry.grn_generated) {
      toast.error("Generate GRN first to enable issuing");
      return;
    }
    setIssueDialog({
      step: 1,
      entry,
      department: "",
      lines: entry.items.map((it, idx) => {
        const issued = Number(entry.issued_quantities?.[String(idx)] || 0);
        const balance = Number(it.actual_quantity) - issued;
        return { item_index: idx, description: it.description, item_code: it.item_code, balance, quantity: 0 };
      }).filter((l) => l.balance > 0),
    });
  };

  const submitIssue = async () => {
    if (!issueDialog.department) return toast.error("Select Issue to Department");
    const lines = issueDialog.lines.filter((l) => Number(l.quantity) > 0);
    if (lines.length === 0) return toast.error("Enter quantity for at least one item");
    try {
      const { data } = await api.post(`/store-entries/${issueDialog.entry.id}/issue`, {
        department: issueDialog.department,
        intender_name: (issueDialog.intender_name || "").trim(),
        items: lines.map((l) => ({
          store_entry_id: issueDialog.entry.id,
          item_index: l.item_index,
          quantity: Number(l.quantity),
        })),
      });
      toast.success(`Issue Slip ${data.slip_number} generated`);
      setIssueDialog(null);
      load();
      navigate(`/issue-slips/print/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Issue failed");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500">Module · Stores</div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">Store</h1>
        <p className="text-sm text-slate-500 mt-1">Receive goods against a PO. Validate actuals, then issue to departments.</p>
      </div>

      {/* PO Lookup + form */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[260px]">
            <Lbl>Enter PO Number</Lbl>
            <input
              data-testid="store-po-input"
              value={poNumber}
              onChange={(e) => setPoNumber(e.target.value.toUpperCase())}
              placeholder="e.g. PO-0001"
              className={`${inputCls} font-mono`}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), fetchPo())}
            />
          </div>
          <button onClick={fetchPo} data-testid="store-fetch-btn" className="bg-[#1D2951] hover:bg-[#2B3A67] text-white px-4 py-2 rounded-md text-sm font-semibold inline-flex items-center gap-2">
            <Search size={14} /> Fetch PO
          </button>
        </div>
        {/* Search bar — filters the Logs/Receipts table by GRN / PO / Supplier / Item */}
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <div className="relative w-full max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="store-log-search"
              value={logQuery}
              onChange={(e) => setLogQuery(e.target.value)}
              placeholder="Search logs by GRN / PO / Supplier / Item…"
              className={`${inputCls} pl-9`}
            />
          </div>
          {/* --- Extra filters (Feb-2026) — narrow the log by any combination --- */}
          <select
            value={logCompany}
            onChange={(e) => setLogCompany(e.target.value)}
            data-testid="store-log-filter-company"
            className={`${inputCls} max-w-[160px]`}
            title="Filter by Company"
          >
            <option value="">All Companies</option>
            {companyOptions.map((c) => (<option key={c} value={c}>{c}</option>))}
          </select>
          <select
            value={logCategory}
            onChange={(e) => setLogCategory(e.target.value)}
            data-testid="store-log-filter-category"
            className={`${inputCls} max-w-[160px]`}
            title="Filter by Item Category"
          >
            <option value="">All Categories</option>
            {categoryOptions.map((c) => (<option key={c} value={c}>{c}</option>))}
          </select>
          <select
            value={logCostCenter}
            onChange={(e) => setLogCostCenter(e.target.value)}
            data-testid="store-log-filter-cc"
            className={`${inputCls} max-w-[160px]`}
            title="Filter by Cost Center"
          >
            <option value="">All Cost Centres</option>
            {costCenterOptions.map((c) => (<option key={c} value={c}>{c}</option>))}
          </select>
          <select
            value={logSupplier}
            onChange={(e) => setLogSupplier(e.target.value)}
            data-testid="store-log-filter-supplier"
            className={`${inputCls} max-w-[180px]`}
            title="Filter by Supplier"
          >
            <option value="">All Suppliers</option>
            {supplierOptions.map((s) => (<option key={s} value={s}>{s}</option>))}
          </select>
          {(logCompany || logCategory || logCostCenter || logSupplier) && (
            <button
              onClick={() => { setLogCompany(""); setLogCategory(""); setLogCostCenter(""); setLogSupplier(""); }}
              data-testid="store-log-clear-filters"
              className="text-xs text-slate-600 hover:text-slate-900 underline"
              title="Clear all filter dropdowns"
            >Clear filters</button>
          )}
        </div>
      </div>

      {/* PO fully received — helpful panel replacing the old dead-end toast.
          Lets the user book multiple supplier bills against the same PO
          (each existing GRN can carry its own supplier invoice). */}
      {poFullyReceived && !edit && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-5" data-testid="store-po-fully-received">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-sm font-semibold text-amber-900">
                PO {poFullyReceived.po.po_number} is fully received — nothing left to inward
              </div>
              <div className="text-xs text-amber-800 mt-1 leading-relaxed">
                Each existing GRN below can be billed independently.
                Multiple supplier invoices can be booked against the <span className="font-semibold">same GRN</span> —
                open Bill Booking and enter the GRN number.
              </div>
            </div>
            <button
              onClick={() => { setPoFullyReceived(null); setPo(null); setPoNumber(""); }}
              data-testid="store-po-fully-received-dismiss"
              className="text-xs text-amber-800 hover:text-amber-900 underline whitespace-nowrap"
            >Dismiss</button>
          </div>
          {poFullyReceived.grns.length > 0 ? (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-amber-100/50 text-amber-900">
                    <th className="px-2 py-2 text-left font-semibold">GRN</th>
                    <th className="px-2 py-2 text-left font-semibold">GRN Date</th>
                    <th className="px-2 py-2 text-left font-semibold">Supplier Invoice</th>
                    <th className="px-2 py-2 text-left font-semibold">Items</th>
                    <th className="px-2 py-2 text-right font-semibold">Actual Total</th>
                    <th className="px-2 py-2 text-right font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {poFullyReceived.grns.map((g) => (
                    <tr key={g.id} className="border-t border-amber-200/60">
                      <td className="px-2 py-2 font-mono font-semibold text-amber-900">{g.grn_number}</td>
                      <td className="px-2 py-2 text-slate-700">{g.grn_date || "—"}</td>
                      <td className="px-2 py-2 text-slate-700">{g.supplier_invoice_number || "—"}</td>
                      <td className="px-2 py-2 text-slate-700 truncate max-w-[360px]" title={g.items_summary}>{g.items_summary}</td>
                      <td className="px-2 py-2 text-right font-mono text-slate-900">₹{Number(g.actual_total || 0).toLocaleString("en-IN")}</td>
                      <td className="px-2 py-2 text-right">
                        <button
                          onClick={() => {
                            // Hand off the GRN to the Bill Booking screen so it
                            // auto-prefills and fetches on landing. Session storage
                            // is used (not a URL param) to keep the URL clean.
                            sessionStorage.setItem("cpms_prefill_grn", g.grn_number);
                            navigate("/account");
                          }}
                          data-testid={`store-po-fully-received-book-${g.grn_number}`}
                          className="inline-flex items-center gap-1 bg-[#1D2951] hover:bg-[#2B3A67] text-white text-xs font-semibold px-3 py-1 rounded"
                        >
                          <Send size={11} /> Book Bill
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="mt-3 text-xs text-amber-800 italic">No GRNs generated yet against this PO.</div>
          )}
        </div>
      )}

      {edit && po && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between gap-4">
            <div>
              <div className="text-sm font-semibold text-slate-900">{edit.po_number} — {edit.id ? "Edit Receipt" : "Receive Goods"}</div>
              <div className="text-xs text-slate-500 mt-0.5">PO Total: <span className="font-mono text-slate-900">₹{poTotal.toLocaleString("en-IN")}</span></div>
            </div>
            {exceeds && <div className="text-xs font-semibold uppercase tracking-wider px-2 py-1 bg-rose-100 text-rose-700 rounded whitespace-nowrap">Actual value exceeds PO total</div>}
          </div>

          {/* Header section: Supplier + Invoice + Vehicle */}
          <div className="p-6 space-y-5">
            {/* Continuation banner — shown when this PO already has prior receipts */}
            {priorReceipts.length > 0 && !edit.id && (
              <div className="rounded-lg border-2 border-amber-300 bg-amber-50/70 p-4" data-testid="store-continuation-banner">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-md bg-amber-200 flex items-center justify-center shrink-0">
                    <Inbox size={18} className="text-amber-800" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-amber-900">
                      Receipt #{priorReceipts.length + 1} for {edit.po_number}
                      <span className="text-xs font-normal text-amber-700 ml-2">· continuation of an earlier inward</span>
                    </div>
                    <div className="text-xs text-amber-800 mt-1">
                      Supplier: <span className="font-semibold">{edit.supplier_name || "—"}</span>
                      {priorReceipts.some((r) => r.grn_number) && (
                        <> · Prior GRN(s): <span className="font-mono font-semibold">{priorReceipts.filter((r) => r.grn_number).map((r) => r.grn_number).join(", ")}</span> · this receipt will get its <span className="font-semibold">OWN new GRN number</span> (bill-wise GRN)</>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {priorReceipts.map((r) => (
                        <span key={r.id} className="text-[10px] font-mono px-2 py-0.5 rounded border border-amber-300 bg-white text-amber-900" title={`Invoice ${r.invoice_number || "—"} · ${r.created_at?.slice(0, 10) || ""}`}>
                          {r.grn_number || `draft #${(r.id || "").slice(0, 6)}`}
                          {r.invoice_number ? ` · ${r.invoice_number}` : ""}
                        </span>
                      ))}
                    </div>
                    <div className="text-[11px] text-amber-800 mt-2 italic">
                      Enter <span className="font-semibold">fresh invoice &amp; vehicle details</span> for this receipt. Each supplier bill produces its OWN separate GRN — one PO can have multiple GRNs, one per bill.
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-700 mb-3">Supplier &amp; Invoice Details</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Supplier Name">
                  <input data-testid="store-supplier-name" className={`${inputCls} bg-slate-50`} readOnly value={edit.supplier_name || ""} />
                </Field>
                <Field label="GST Number">
                  <input data-testid="store-supplier-gst" className={`${inputCls} bg-slate-50 font-mono`} readOnly value={edit.supplier_gst || ""} />
                </Field>
                <Field label="Invoice Number" required>
                  <input
                    data-testid="store-invoice-number"
                    className={inputCls}
                    placeholder="e.g. INV-2025-001"
                    value={edit.invoice_number || ""}
                    onChange={(e) => setEdit({ ...edit, invoice_number: e.target.value })}
                  />
                </Field>
                <Field label="Invoice Date">
                  <input
                    data-testid="store-invoice-date"
                    type="date"
                    className={inputCls}
                    value={edit.invoice_date || ""}
                    onChange={(e) => setEdit({ ...edit, invoice_date: e.target.value })}
                  />
                </Field>
                <Field label="Vehicle Number">
                  <input
                    data-testid="store-vehicle-number"
                    className={`${inputCls} uppercase`}
                    placeholder="e.g. KA-01-AB-1234"
                    value={edit.vehicle_number || ""}
                    onChange={(e) => setEdit({ ...edit, vehicle_number: e.target.value.toUpperCase() })}
                  />
                </Field>
                <Field label="Challan / DC No.">
                  <input
                    data-testid="store-challan-dc"
                    className={inputCls}
                    placeholder="Delivery Challan number"
                    value={edit.challan_dc || ""}
                    onChange={(e) => setEdit({ ...edit, challan_dc: e.target.value })}
                  />
                </Field>
                <Field label="GRN / Inward Date">
                  <input
                    data-testid="store-grn-date"
                    type="date"
                    className={`${inputCls} font-mono`}
                    value={edit.grn_date || ""}
                    onChange={(e) => setEdit({ ...edit, grn_date: e.target.value })}
                  />
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    Defaults to today. Back-date to push this GRN&apos;s cost into a prior month on Dashboard / MR / Reports.
                  </div>
                </Field>
              </div>
            </div>

            {/* Per-item cards */}
            <div className="space-y-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-700">Items</div>
              {edit.items.map((it, idx) => {
                const balanceQty = Number((Number(it.po_quantity || 0) - Number(it.actual_quantity || 0)).toFixed(3));
                const included = it.include !== false;
                return (
                  <div key={idx} className={`border rounded-lg p-4 space-y-3 transition-all ${included ? "border-slate-200 bg-slate-50/40" : "border-amber-300 bg-amber-50/40 opacity-80"}`} data-testid={`store-item-card-${idx}`}>
                    {/* Big visible Include checkbox — defaults ON. Uncheck to keep
                        the item in BALANCE (it stays pending on this PO). */}
                    <label className={`flex items-start gap-3 cursor-pointer select-none rounded-md px-3 py-2 border-2 ${included ? "border-emerald-400 bg-emerald-50" : "border-amber-400 bg-amber-50"}`} data-testid={`store-include-label-${idx}`}>
                      <input
                        type="checkbox"
                        checked={included}
                        onChange={(e) => updateItem(idx, { include: e.target.checked })}
                        className="mt-0.5 h-5 w-5 rounded border-slate-400 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                        data-testid={`store-include-checkbox-${idx}`}
                      />
                      <div className="flex-1">
                        <div className={`text-sm font-semibold ${included ? "text-emerald-900" : "text-amber-900"}`}>
                          {included ? "Receiving this item" : "Skip — keep in BALANCE"}
                        </div>
                        <div className="text-[11px] text-slate-600 mt-0.5">
                          {included
                            ? "Enter the actual received quantity below. If actual < PO Qty, the remainder still stays in Balance."
                            : "This item will not be inwarded now. It remains pending on the PO and shows as BALANCE in the PO Log."}
                        </div>
                      </div>
                    </label>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Item {idx + 1}</div>
                    {/* Row: Description | Item Code | HSN Code */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Field label="Item Description">
                        <input className={`${inputCls} bg-white`} readOnly value={it.description} />
                      </Field>
                      <Field label="Item Code">
                        <input className={`${inputCls} bg-white font-mono`} readOnly value={it.item_code || "—"} />
                      </Field>
                      <Field label="HSN Code">
                        <input className={`${inputCls} bg-white font-mono`} readOnly value={it.hsn_code || "—"} />
                      </Field>
                    </div>
                    {/* Row: Unit | PO Qty | PO Price */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Field label="Unit">
                        <input className={`${inputCls} bg-white`} readOnly value={it.unit || "—"} />
                      </Field>
                      <Field label="PO Quantity">
                        <input className={`${inputCls} bg-white`} readOnly value={it.po_quantity} />
                      </Field>
                      <Field label="PO Price">
                        <input className={`${inputCls} bg-white font-mono`} readOnly value={`₹${Number(it.po_price || 0)}`} />
                      </Field>
                    </div>
                    {/* Row: Actual Qty | Actual Price | Taxation */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Field label="Actual Quantity" required>
                        <input
                          type="number" min="0" step="0.01" inputMode="decimal"
                          className={inputCls}
                          value={it.actual_quantity}
                          onChange={(e) => updateItem(idx, { actual_quantity: e.target.value === "" ? 0 : Number(e.target.value) })}
                          data-testid={`store-actual-qty-${idx}`}
                          disabled={!included}
                        />
                      </Field>
                      <Field label="Actual Price (per unit)" required>
                        <input
                          type="number" min="0" step="0.01" inputMode="decimal"
                          className={inputCls}
                          value={it.actual_price}
                          onChange={(e) => updateItem(idx, { actual_price: e.target.value === "" ? 0 : Number(e.target.value) })}
                          data-testid={`store-actual-price-${idx}`}
                          disabled={!included}
                        />
                      </Field>
                      <Field label="Taxation (%)">
                        <input
                          type="number" min="0" step="0.01" inputMode="decimal"
                          className={inputCls}
                          value={it.taxation}
                          onChange={(e) => updateItem(idx, { taxation: e.target.value === "" ? 0 : Number(e.target.value) })}
                          data-testid={`store-taxation-${idx}`}
                          placeholder="e.g. 18"
                          disabled={!included}
                        />
                        {Number(it.taxation || 0) > 0 && (
                          <div className="text-[10px] text-slate-500 mt-1 font-mono">= ₹{Number(it.tax_amount || 0).toFixed(2)} on ₹{Number(it.actual_value || 0).toFixed(2)}</div>
                        )}
                      </Field>
                    </div>
                    {/* Row: Actual Value | Gross Value | Balance (calculated) */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Field label="Actual Value (Qty × Price)">
                        <div className={`${inputCls} bg-emerald-50 border-emerald-200 font-mono text-slate-900`} data-testid={`store-actual-value-${idx}`}>
                          ₹{Number(it.actual_value || 0).toFixed(2)}
                        </div>
                      </Field>
                      <Field label="Gross Value (Actual + Tax %)">
                        <div className={`${inputCls} bg-emerald-50 border-emerald-200 font-mono text-slate-900`} data-testid={`store-gross-value-${idx}`}>
                          ₹{Number(it.gross_value || 0).toFixed(2)}
                        </div>
                      </Field>
                      <Field label="Balance Qty (PO Qty − Actual Qty)">
                        <div className={`${inputCls} ${balanceQty < 0 ? "bg-rose-50 border-rose-200 text-rose-700" : "bg-slate-100"} font-mono`} data-testid={`store-line-balance-${idx}`}>
                          {balanceQty} {it.unit || ""}
                        </div>
                      </Field>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Totals summary */}
            <div className="bg-[#1D2951]/5 border border-[#1D2951]/15 rounded-lg p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">PO Total</div>
                <div className="font-mono text-base text-slate-900 mt-1">₹{poTotal.toLocaleString("en-IN")}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total Actual Value</div>
                <div className="font-mono text-base text-slate-900 mt-1" data-testid="store-total-actual">₹{totalActual.toFixed(2)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total Gross Value</div>
                <div className="font-mono text-base text-slate-900 mt-1" data-testid="store-total-gross">₹{totalGross.toFixed(2)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Balance</div>
                <div className={`font-mono text-base mt-1 ${(poTotal - totalGross) < 0 ? "text-rose-700" : "text-slate-900"}`} data-testid="store-total-balance">
                  ₹{(poTotal - totalGross).toFixed(2)}
                </div>
              </div>
            </div>

            <div>
              <Lbl>Notes</Lbl>
              <textarea value={edit.notes || ""} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} className={`${inputCls} min-h-[60px]`} />
            </div>
          </div>
          <div className="px-6 py-3 border-t border-slate-100 bg-slate-50 flex justify-end gap-2 flex-wrap">
            <button onClick={() => { setEdit(null); setPo(null); setPoNumber(""); setPriorReceipts([]); }} data-testid="store-cancel-btn" className="border border-slate-300 px-4 py-2 rounded-md text-sm">Cancel</button>
            <button onClick={() => save()} data-testid="store-save-btn" disabled={exceeds} className="border border-[#1D2951] text-[#1D2951] hover:bg-[#1D2951]/5 disabled:opacity-50 px-4 py-2 rounded-md text-sm font-semibold inline-flex items-center gap-2"><Save size={14} /> Save</button>
            <button onClick={() => save({ generateGrn: true })} data-testid="store-save-grn-btn" disabled={exceeds} className="bg-[#1D2951] hover:bg-[#2B3A67] disabled:opacity-50 text-white px-4 py-2 rounded-md text-sm font-semibold inline-flex items-center gap-2"><FileCheck size={14} /> Save &amp; Generate GRN</button>
          </div>
        </div>
      )}

      {/* Logs / Receipts (flat, per-item) */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-sm font-semibold text-slate-900">Logs / Receipts</div>
            <div className="text-xs text-slate-500">Real-time receipts · Balance = PO Total − Gross Value</div>
          </div>
          <div className="inline-flex items-center gap-3 flex-wrap">
            {/* Total Gross Value across the currently-visible (search + date filtered) rows */}
            <div
              data-testid="store-log-total-value"
              className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-1.5"
              title="Sum of Gross Value across the filtered receipts below"
            >
              <span className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Total Value</span>
              <span
                className="font-mono text-sm font-semibold text-emerald-800"
                data-testid="store-log-total-value-amount"
              >
                ₹{Number(logTotalGrossValue).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
              </span>
            </div>
            {/* Duration filter — filters visible rows + total by grn_date range */}
            <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-md px-3 py-1.5" data-testid="store-log-date-range">
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">From</span>
              <input
                type="date"
                value={logFromDate}
                onChange={(e) => setLogFromDate(e.target.value)}
                data-testid="store-log-date-from"
                className="bg-transparent text-xs font-mono text-slate-900 focus:outline-none"
              />
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold border-l border-slate-200 pl-2">To</span>
              <input
                type="date"
                value={logToDate}
                onChange={(e) => setLogToDate(e.target.value)}
                data-testid="store-log-date-to"
                className="bg-transparent text-xs font-mono text-slate-900 focus:outline-none"
              />
              {(logFromDate || logToDate) && (
                <button
                  onClick={() => { setLogFromDate(""); setLogToDate(""); }}
                  data-testid="store-log-date-clear"
                  className="text-xs font-semibold text-[#1D2951] hover:underline ml-1"
                  title="Clear date range"
                >
                  Clear
                </button>
              )}
            </div>
            <TableExportButton
              testIdPrefix="store-export"
              filename="store_log"
              rows={filteredLogEntries.flatMap((e) => {
                const d = (e.grn_date || e.created_at || "").slice(0, 10);
                const monthLbl = d ? new Date(d).toLocaleString("en-IN", { month: "short", year: "numeric" }) : "";
                return (e.items || []).map((it, idx) => ({
                  month: monthLbl,
                  date: d,
                  po_number: e.po_number,
                  grn_number: e.grn_number || "",
                  company_name: e.company_name || "",
                  cost_center: e.cost_center || "",
                  category: it.category || "",
                  supplier_name: e.supplier_name || "",
                  invoice_number: e.invoice_number || "",
                  invoice_date: e.invoice_date || "",
                  vehicle_number: e.vehicle_number || "",
                  item_code: it.item_code || "",
                  description: it.description || "",
                  actual_quantity: it.actual_quantity,
                  unit: it.unit,
                  gross_value: it.gross_value,
                  actual_total: idx === 0 ? e.actual_total : "",
                  balance_value: idx === 0 ? e.balance_value : "",
                }));
              })}
              dateField="date"
              filterField="company_name"
              filterOptions={Array.from(new Set((entries || []).map((e) => e.company_name || "").filter(Boolean))).sort()}
              filterLabel="Company (tick to include)"
              columns={[
                { key: "month", label: "Month" },
                { key: "date", label: "Date" },
                { key: "po_number", label: "PO No" },
                { key: "grn_number", label: "GRN Number" },
                { key: "company_name", label: "Company" },
                { key: "cost_center", label: "Cost Centre" },
                { key: "category", label: "Category" },
                { key: "supplier_name", label: "Supplier" },
                { key: "invoice_number", label: "Supplier Invoice" },
                { key: "invoice_date", label: "Invoice Date" },
                { key: "vehicle_number", label: "Vehicle" },
                { key: "item_code", label: "Item Code" },
                { key: "description", label: "Item" },
                { key: "actual_quantity", label: "Qty" },
                { key: "unit", label: "Unit" },
                { key: "gross_value", label: "Gross Value" },
                { key: "actual_total", label: "Actual Total" },
                { key: "balance_value", label: "Balance" },
              ]}
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
                <th className="text-left px-4 py-3">Month</th>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-left px-4 py-3">PO No.</th>
                <th className="text-left px-4 py-3">GRN Number</th>
                <th className="text-left px-4 py-3">Company</th>
                <th className="text-left px-4 py-3">Cost Centre</th>
                <th className="text-left px-4 py-3">Category</th>
                <th className="text-left px-4 py-3">Supplier</th>
                <th className="text-left px-4 py-3">Item</th>
                <th className="text-right px-4 py-3">Actual Value</th>
                <th className="text-right px-4 py-3">Gross Value</th>
                <th className="text-right px-4 py-3">Balance</th>
                <th className="text-right px-4 py-3">Quantity</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="store-list">
              {(() => {
                const filteredEntries = filteredLogEntries;
                if (filteredEntries.length === 0) {
                  return (
                    <tr><td colSpan="14" className="text-center py-16 text-slate-400">
                      <Inbox size={28} className="mx-auto mb-2 opacity-40" />
                      {(logQuery || logFromDate || logToDate) ? "No receipts match your filters" : "No receipts yet"}
                    </td></tr>
                  );
                }
                return filteredEntries.flatMap((e) =>
                (e.items || []).map((it, idx) => {
                  const grossValue = Number(it.gross_value || 0);
                  const balance = Number(e.balance_value || 0);
                  const isFirstRow = idx === 0;
                  const rowDate = (e.grn_date || e.created_at || "").slice(0, 10);
                  const monthLbl = rowDate ? new Date(rowDate).toLocaleString("en-IN", { month: "short", year: "numeric" }) : "";
                  return (
                    <tr key={`${e.id}-${idx}`} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`store-row-${e.id}-${idx}`}>
                      <td className="px-4 py-3 text-xs align-top">{isFirstRow ? monthLbl : ""}</td>
                      <td className="px-4 py-3 font-mono text-xs align-top">{isFirstRow ? rowDate : ""}</td>
                      <td className="px-4 py-3 font-mono font-semibold align-top">{isFirstRow ? e.po_number : ""}</td>
                      <td className="px-4 py-3 align-top">
                        {isFirstRow && (e.grn_generated ? (
                          <span className="font-mono text-xs px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">{e.grn_number}</span>
                        ) : (
                          <span className="text-xs text-slate-400 italic">pending</span>
                        ))}
                      </td>
                      <td className="px-4 py-3 align-top text-xs">{isFirstRow ? (e.company_name || "—") : ""}</td>
                      <td className="px-4 py-3 align-top text-xs">{isFirstRow ? (e.cost_center || "—") : ""}</td>
                      <td className="px-4 py-3 align-top text-xs">{it.category || "—"}</td>
                      <td className="px-4 py-3 align-top">{isFirstRow ? e.supplier_name : ""}</td>
                      <td className="px-4 py-3">
                        <div>{it.description}</div>
                        {it.item_code && <div className="text-[10px] text-slate-500 font-mono">{it.item_code}</div>}
                      </td>
                      <td className="px-4 py-3 text-right font-mono align-top">{isFirstRow ? `₹${Number(e.actual_total || 0).toLocaleString("en-IN")}` : ""}</td>
                      <td className="px-4 py-3 text-right font-mono">₹{grossValue.toLocaleString("en-IN")}</td>
                      <td className="px-4 py-3 text-right font-mono font-semibold align-top">{isFirstRow ? `₹${balance.toLocaleString("en-IN")}` : ""}</td>
                      <td className="px-4 py-3 text-right font-mono">{Number(it.actual_quantity || 0)} {it.unit}</td>
                      <td className="px-4 py-3 text-right align-top">
                        {isFirstRow && (
                          <div className="inline-flex gap-1 flex-wrap justify-end">
                            {!e.grn_generated && (
                              <button title="Generate GRN" onClick={() => generateGrnFor(e)} data-testid={`gen-grn-${e.id}`} className="px-2 py-1 hover:bg-emerald-50 rounded text-emerald-700 text-xs font-semibold inline-flex items-center gap-1 border border-emerald-200"><FileCheck size={12} /> GRN</button>
                            )}
                            {e.grn_generated && (
                              <button title="Print GRN" onClick={() => navigate(`/grn/print/${e.id}`)} data-testid={`print-grn-${e.id}`} className="px-2 py-1 hover:bg-slate-100 rounded text-slate-700 text-xs font-semibold inline-flex items-center gap-1"><Printer size={12} /> GRN</button>
                            )}
                            <button title="Issue Slip" onClick={() => startIssue(e)} data-testid={`issue-${e.id}`} disabled={!e.grn_generated} className="px-2 py-1 hover:bg-[#1D2951]/10 disabled:opacity-40 rounded text-[#1D2951] text-xs font-semibold inline-flex items-center gap-1"><Send size={12} /> Issue</button>
                            <button title="Edit" onClick={() => editExisting(e)} className="p-1.5 hover:bg-slate-100 rounded text-slate-600"><Edit size={14} /></button>
                            {user?.role === "admin" && (
                              <button title="Delete" onClick={() => del(e.id)} className="p-1.5 hover:bg-rose-50 rounded text-rose-600"><Trash2 size={14} /></button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
                );
              })()}
            </tbody>
          </table>
        </div>
      </div>

      {/* Issue Dialog (2 steps) */}
      <Dialog open={!!issueDialog} onOpenChange={(o) => !o && setIssueDialog(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{issueDialog?.step === 1 ? "Issue to Department" : "Generate Issue Slip"}</DialogTitle>
            <DialogDescription>
              {issueDialog?.step === 1
                ? "Select the destination department for this issue."
                : "Specify the quantity to issue for each item, then generate the slip."}
            </DialogDescription>
          </DialogHeader>
          {issueDialog?.step === 1 && (
            <div className="space-y-3">
              <div>
                <Lbl>Issue to Department</Lbl>
                <SearchableSelect
                  testid="issue-department"
                  options={departments.map((d) => ({ value: d, label: d }))}
                  value={issueDialog.department}
                  onChange={(v) => setIssueDialog({ ...issueDialog, department: v })}
                  placeholder="Choose department"
                  allowCustom
                />
              </div>
              <div>
                <Lbl>Indentor Name</Lbl>
                <SearchableSelect
                  testid="issue-intender-name"
                  options={intenders.map((i) => ({ value: i.name, label: i.name }))}
                  value={issueDialog.intender_name || ""}
                  onChange={(v) => setIssueDialog({ ...issueDialog, intender_name: v })}
                  placeholder="Search & select indentor"
                  allowCustom
                />
              </div>
            </div>
          )}
          {issueDialog?.step === 2 && (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {issueDialog.lines.map((l, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 p-2 border border-slate-200 rounded">
                  <div className="col-span-6 text-sm">
                    <div className="font-medium">{l.description}</div>
                    <div className="text-[11px] text-slate-500 font-mono">{l.item_code} · balance {l.balance}</div>
                  </div>
                  <div className="col-span-6">
                    <input
                      type="number"
                      min="0"
                      max={l.balance}
                      step="0.01"
                      value={l.quantity}
                      onChange={(e) => {
                        const lines = [...issueDialog.lines];
                        lines[i] = { ...lines[i], quantity: Number(e.target.value) };
                        setIssueDialog({ ...issueDialog, lines });
                      }}
                      className={inputCls}
                      placeholder="Qty to issue"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            {issueDialog?.step === 1 && (
              <>
                <button onClick={() => setIssueDialog(null)} className="px-4 py-2 rounded-md border border-slate-300 text-sm">Cancel</button>
                <button
                  onClick={() => setIssueDialog({ ...issueDialog, step: 2 })}
                  data-testid="issue-next-btn"
                  disabled={!issueDialog.department}
                  className="bg-[#1D2951] hover:bg-[#2B3A67] disabled:opacity-50 text-white px-4 py-2 rounded-md text-sm font-semibold"
                >Next →</button>
              </>
            )}
            {issueDialog?.step === 2 && (
              <>
                <button onClick={() => setIssueDialog({ ...issueDialog, step: 1 })} className="px-4 py-2 rounded-md border border-slate-300 text-sm">← Back</button>
                <button onClick={submitIssue} data-testid="issue-generate-btn" className="bg-[#1D2951] hover:bg-[#2B3A67] text-white px-4 py-2 rounded-md text-sm font-semibold inline-flex items-center gap-2">
                  <Printer size={14} /> Generate Issue Slip
                </button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const inputCls = "w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D2951]/20 focus:border-[#1D2951] bg-white";
const Lbl = ({ children }) => (<div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">{children}</div>);
const Field = ({ label, required, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">
      {label}{required && <span className="text-rose-600 ml-0.5">*</span>}
    </div>
    {children}
  </div>
);
